/**
 * When opening the add-giftee wizard, pass { state: { from: pathname } } so the
 * shell close (X) can return to the screen the user came from. Successful
 * completion navigates to the new giftee's hub and stamps the same `from` onto
 * the hub location so its X does not fall back through history onto `/` or
 * `/auth` (Try Gift Gaff leaves the landing page under the wizard).
 */

const DEFAULT_WIZARD_RETURN = "/home";

const AUTH_LANDING_PATHNAMES = new Set(["/", "/auth", "/signin", "/signup"]);

/**
 * @param {unknown} p
 * @returns {p is string}
 */
export function isSafeWizardReturnPath(p) {
  return (
    typeof p === "string" &&
    p.length > 0 &&
    p.startsWith("/") &&
    !p.startsWith("//")
  );
}

/**
 * Marketing start screen — guests must not be sent here by in-app close.
 *
 * @param {unknown} p
 * @returns {boolean}
 */
export function isAuthLandingPath(p) {
  if (!isSafeWizardReturnPath(p)) return false;
  const pathname = p.split(/[?#]/, 1)[0];
  return AUTH_LANDING_PATHNAMES.has(pathname);
}

/**
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {string} from - pathname to return to when the wizard is dismissed
 * @param {import("react-router-dom").NavigateOptions} [options]
 */
export function openCreateGifteeWizard(navigate, from, options = {}) {
  navigate("/create-giftee", {
    ...options,
    state: { ...options.state, from },
  });
}

/**
 * @param {import("react-router-dom").Location} location
 * @returns {string}
 */
export function getWizardReturnPath(location) {
  const from = location?.state && location.state.from;
  if (isSafeWizardReturnPath(from) && !isAuthLandingPath(from)) return from;
  return DEFAULT_WIZARD_RETURN;
}

/**
 * Hub X: honor `state.from` when the wizard (or another opener) stamped a
 * return path. Callers should `navigate(-1)` when this returns null so the
 * usual previous-page close still works.
 *
 * @param {import("react-router-dom").Location} [location]
 * @returns {string | null}
 */
export function getGifteeHubClosePath(location) {
  const from = location?.state && location.state.from;
  if (!isSafeWizardReturnPath(from)) return null;
  if (isAuthLandingPath(from)) return DEFAULT_WIZARD_RETURN;
  return from;
}

/**
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {import("react-router-dom").Location} [location]
 */
export function closeGifteeHub(navigate, location) {
  const path = getGifteeHubClosePath(location);
  if (path) {
    navigate(path);
    return;
  }
  navigate(-1);
}

/**
 * Replace the wizard with the new hub and keep the screen the wizard would
 * have returned to, so hub close does not pop back to the landing page.
 *
 * @param {import("react-router-dom").Location} location
 * @returns {import("react-router-dom").NavigateOptions}
 */
export function gifteeHubAfterCreateOptions(location) {
  return {
    replace: true,
    state: { from: getWizardReturnPath(location) },
  };
}

/**
 * @param {string} gifteeId
 * @returns {string}
 */
export function getGifteeHubPath(gifteeId) {
  return `/giftee/${gifteeId}`;
}
